'use client';

import React from 'react';

import { HStack } from '@chakra-ui/react';
import { HistoryIcon, Plus, X } from 'lucide-react';

import { IconButton } from '@/components/core';
import { FONTS_OPACITY } from '@/constant';
import { cn } from '@/lib/utils';

interface Props {
  className?: string;
  onSessionChangeClick?: () => void;
  onNewChatClick?: () => void;
  onClose?: () => void;
  closeLabel?: string;
  closeDataCy?: string;
  isSessionsMode?: boolean;
  isSessionsEnabled?: boolean;
  currentSessionId?: string | null;
  isChatThinking?: boolean;
  isSessionsLoading?: boolean;
}

const chatActionButtonProps = {
  variant: 'ghost' as const,
  colorScheme: 'gray' as const,
  size: 'sm' as const,
  rounded: 'lg' as const,
  color: 'whiteAlpha.700',
  _hover: {
    bg: 'whiteAlpha.100',
    color: 'whiteAlpha.900',
  },
  _active: {
    bg: 'whiteAlpha.200',
  },
  _disabled: {
    opacity: FONTS_OPACITY,
    cursor: 'not-allowed',
  },
};

const ChatMenu: React.FC<Props> = ({
  className,
  onSessionChangeClick,
  onNewChatClick,
  onClose,
  closeLabel = 'Close chat',
  closeDataCy,
  isSessionsMode,
  isSessionsEnabled,
  currentSessionId,
  isChatThinking,
  isSessionsLoading,
}) => {
  const canStartNewChat = Boolean(currentSessionId) && !isChatThinking;
  const canViewSessions =
    Boolean(isSessionsEnabled) && !isChatThinking && !isSessionsLoading;

  return (
    <HStack gap={0.5} className={cn(className)}>
      <IconButton
        {...chatActionButtonProps}
        aria-label='Start a new chat'
        title='Start a new chat'
        icon={<Plus size={16} />}
        onClick={onNewChatClick}
        isDisabled={!canStartNewChat}
      />
      <IconButton
        {...chatActionButtonProps}
        aria-label='View sessions'
        title='View sessions'
        icon={<HistoryIcon size={16} />}
        onClick={onSessionChangeClick}
        isDisabled={!canViewSessions}
        isLoading={isSessionsLoading}
        bg={isSessionsMode ? 'whiteAlpha.100' : undefined}
      />
      {onClose ? (
        <IconButton
          {...chatActionButtonProps}
          aria-label={closeLabel}
          title={closeLabel}
          icon={<X size={16} />}
          onClick={onClose}
          data-cy={closeDataCy}
        />
      ) : null}
    </HStack>
  );
};

export { ChatMenu };
